import configuration from '~/configuration'
import type { IUserInformation } from '~/core/@types/global'
import {
  SESSION_COOKIE_CURRENT_TENANT,
  SESSION_COOKIE_IS_DISABLE_SIDEBAR_ITEM
} from '~/core/constants/cookies'
import { getPathFromReturnUrl, shouldPreserveInternalReturnUrl } from '~/lib/utilities/pathname'

export function redirectTo({
  returnUrl,
  redirectPath
}: {
  returnUrl?: string
  redirectPath: string
}) {
  if (returnUrl) {
    const cleanReturnUrl = getPathFromReturnUrl(returnUrl)
    const queryParams = new URLSearchParams({
      returnUrl: cleanReturnUrl ?? configuration.path.default
    })
    const destination = `${redirectPath}?${queryParams}`

    return {
      redirect: {
        permanent: false,
        destination
      }
    }
  }

  return {
    redirect: {
      permanent: false,
      destination: redirectPath
    }
  }
}

function checkRequisitionsAccess(
  resolvedUrl: string,
  isKeepCheckQuery: boolean,
  query: { [key: string]: string | string[] | undefined }
): boolean {
  const isRequisitionsFromMiddleware =
    isKeepCheckQuery && resolvedUrl.includes(configuration.path.requisitions.list)
  const isRequisitionsFromApp =
    resolvedUrl.includes(configuration.path.requisitions.list) && !!query.id && !!query.userToken

  return isRequisitionsFromApp || isRequisitionsFromMiddleware
}

function validateTenantFlow(
  user: IUserInformation,
  resolvedUrl: string,
  currentTenant: string | undefined,
  shouldKeepReturnUrl: boolean
) {
  const tenantCount = user?.tenants?.data?.length || 0

  // No tenants - redirect to onboarding
  if (tenantCount === 0 && !resolvedUrl.startsWith(configuration.path.noAssociatedCompany)) {
    return redirectTo({
      returnUrl: shouldKeepReturnUrl ? resolvedUrl : undefined,
      redirectPath: configuration.path.noAssociatedCompany
    })
  }

  // Has tenants but on onboarding page - redirect to default
  if (tenantCount >= 1 && resolvedUrl.startsWith(configuration.path.onboarding)) {
    return redirectTo({
      redirectPath: configuration.path.default
    })
  }

  // Multiple tenants without selection - redirect to select page
  if (
    tenantCount > 1 &&
    !currentTenant &&
    !resolvedUrl.startsWith(configuration.path.selectCompany)
  ) {
    return redirectTo({
      returnUrl: shouldKeepReturnUrl ? resolvedUrl : undefined,
      redirectPath: configuration.path.selectCompany
    })
  }

  return null
}

function checkPlanExpiration({
  resolvedUrl,
  planInfo,
  user
}: {
  resolvedUrl: string
  planInfo?: { isExpired: boolean }
  user: IUserInformation
}) {
  if (!planInfo || resolvedUrl.startsWith(configuration.path.selectCompany)) {
    return null
  }

  const planErrorPages = [
    configuration.path.errorExpiredPlan,
    configuration.path.settings.plans_expired,
    configuration.path.error406
  ]

  if (planInfo.isExpired && resolvedUrl.startsWith('/download')) {
    return redirectTo({
      redirectPath: configuration.path.error404
    })
  }

  // Plan expired and user is owner
  if (
    planInfo.isExpired &&
    user.ownTenant &&
    !resolvedUrl.startsWith(configuration.path.settings.plans_expired)
  ) {
    return redirectTo({
      redirectPath: configuration.path.settings.plans_expired
    })
  }

  // Plan expired and user is not owner
  if (
    planInfo.isExpired &&
    !user.ownTenant &&
    !resolvedUrl.startsWith(configuration.path.errorExpiredPlan)
  ) {
    return redirectTo({
      redirectPath: configuration.path.errorExpiredPlan
    })
  }

  // Plan not expired but on error pages
  if (planErrorPages.some((url) => resolvedUrl.startsWith(url)) && !planInfo.isExpired) {
    return redirectTo({
      redirectPath: configuration.path.default
    })
  }

  return null
}

export const handleAuthenticationInternalFlow = ({
  cookies,
  user,
  resolvedUrl,
  query,
  isKeepCheckQuery = false,
  planInfo
}: {
  cookies: { [key: string]: string }
  user?: IUserInformation
  resolvedUrl: string
  query: { [key: string]: string | string[] | undefined }
  isKeepCheckQuery?: boolean
  planInfo?: { isExpired: boolean }
}) => {
  const currentTenant = cookies[SESSION_COOKIE_CURRENT_TENANT]
  const companyBlocked = cookies[SESSION_COOKIE_IS_DISABLE_SIDEBAR_ITEM] || false
  const shouldKeepReturnUrl = shouldPreserveInternalReturnUrl(resolvedUrl)

  try {
    // 1. Check requisitions access - highest priority
    if (checkRequisitionsAccess(resolvedUrl, isKeepCheckQuery, query)) {
      return {
        props: {
          user
        }
      }
    }

    // 2. Unauthenticated user
    if (!user || !user?.id) {
      return redirectTo({
        returnUrl: resolvedUrl,
        redirectPath: configuration.path.login
      })
    }

    // 3. Block company page - special handling
    if (resolvedUrl.startsWith(configuration.path.tenantBlocked)) {
      return {
        props: {
          user
        }
      }
    }

    // 4. Validate tenant flow
    const tenantResult = validateTenantFlow(user, resolvedUrl, currentTenant, shouldKeepReturnUrl)
    if (tenantResult) return tenantResult

    // 5. Check plan expiration
    const planResult = checkPlanExpiration({ resolvedUrl, planInfo, user })
    if (planResult) return planResult

    // 6. Handle onboarding flow
    if (user?.onboarding === true && !companyBlocked && !!currentTenant) {
      const allowedPaths = [configuration.path.welcome, configuration.path.error406]
      if (!allowedPaths.some((url) => resolvedUrl.startsWith(url))) {
        return redirectTo({
          returnUrl: shouldKeepReturnUrl ? resolvedUrl : undefined,
          redirectPath: configuration.path.welcome
        })
      }

      return null
    }

    // 7. Handle completed onboarding
    if (user?.onboarding === false) {
      if (resolvedUrl.startsWith(configuration.path.welcome)) {
        return redirectTo({
          redirectPath: shouldKeepReturnUrl ? configuration.path.default : configuration.path.login
        })
      }

      return null
    }

    // 8. All checks passed - allow page access
    return {
      props: {
        user
      }
    }
  } catch (e) {
    // Error handling - redirect to login
    return redirectTo({
      returnUrl: resolvedUrl,
      redirectPath: shouldKeepReturnUrl ? configuration.path.default : configuration.path.login
    })
  }
}
